import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from 'lucide-react';
import "flag-icons/css/flag-icons.min.css";
import { setCurrency, setLanguage } from "shared/stores/authSlice";

const LanguageCurrencyDropdown = ({ isMobile }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const currentLanguage = useSelector((state) => state.auth.language);
    const currentCurrency = useSelector((state) => state.auth.currency);

    const languages = [
        { code: 'da', label: 'Dansk', flag: 'dk' },
        { code: 'en', label: 'English', flag: 'gb' },
        { code: 'es', label: 'Español', flag: 'es' },
        { code: 'sv', label: 'Svenska', flag: 'se' },
        { code: 'no', label: 'Norsk', flag: 'no' }
    ];

    const currencies = [
        { code: 'EUR', label: 'EUR' },
        { code: 'DKK', label: 'DKK' },
        { code: 'SEK', label: 'SEK' },
        { code: 'NOK', label: 'NOK' },
        { code: 'USD', label: 'USD' },
        { code: 'GBP', label: 'GBP' }
    ];

    const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Ensure language is set correctly when component mounts
    useEffect(() => {
        if (currentLanguage && i18n.language !== currentLanguage) {
            i18n.changeLanguage(currentLanguage);
        }
    }, [currentLanguage, i18n]);

    const handleLanguageChange = (code) => {
        dispatch(setLanguage(code));
        i18n.changeLanguage(code);
        setIsOpen(false);
    };

    const handleCurrencyChange = (code) => {
        dispatch(setCurrency(code));
        setIsOpen(false);
    };

    return (
        <div className="relative" id="lang-currency-dropdown" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 ${isMobile ? 'px-3 py-2' : 'px-3 py-2'} text-sm font-semibold text-gray-700 hover:text-gray-900 border rounded-md`}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <span className={`fi fi-${currentLang.flag} fis rounded-sm ${isMobile ? 'text-lg' : ''}`} />
                {!isMobile && <span className="border-l pl-2">{currentCurrency}</span>}
                <ChevronDownIcon className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
            </button>

            {isOpen && (
                <div className={`absolute ${isMobile ? 'left-0' : 'right-0'} mt-2 ${isMobile ? 'w-56' : 'w-48'} rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50`}>
                    <div className="py-1">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500">Language</div>
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => handleLanguageChange(lang.code)}
                                className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${currentLanguage === lang.code ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <span className={`fi fi-${lang.flag} fis rounded-sm`} />
                                <span>{lang.label}</span>
                            </button>
                        ))}

                        <div className="mt-2 border-t border-gray-100">
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500">Currency</div>
                            {currencies.map((currency) => (
                                <button
                                    key={currency.code}
                                    onClick={() => handleCurrencyChange(currency.code)}
                                    className={`flex w-full items-center px-4 py-2 text-sm ${currentCurrency === currency.code ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                >
                                    {currency.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LanguageCurrencyDropdown;